import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  Box,
  Flex,
  Text,
  Badge,
  Link,
  Divider,
  HStack,
  VStack,
  Image,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
} from '@chakra-ui/react';
import { ChevronRightIcon, ExternalLinkIcon } from '@chakra-ui/icons';

import { getMarketplaceLogo } from 'components/marketplaceLogo';
import { useColors } from './colors';
import {
  fulfillmentStatusBadge,
  orderMainStatusBadge,
  paymentStatusBadge,
  formatOrderDate,
  formatMoney,
} from '../../utils/orderStatus.util';

const marketplaceOrderUrl = (order) => {
  if (!order) return null;
  if (order.marketplace === 'ebay') {
    return `https://www.ebay.com/sh/ord/details?orderid=${order.marketplaceOrderId}`;
  }
  if (order.marketplace === 'amazon') {
    return `https://sellercentral.amazon.com/orders-v3/order/${order.marketplaceOrderId}`;
  }
  return null;
};

const OrderHeader = ({ order }) => {
  const { bgColor, borderColor, textColor, headingColor, subTextColor } = useColors();

  const mainStatus = orderMainStatusBadge(order);
  const fulfillment = fulfillmentStatusBadge(order.orderFulfillmentStatus || order.orderStatus);
  const payment = paymentStatusBadge(order.orderPaymentStatus || 'PAID');
  const extUrl = marketplaceOrderUrl(order);

  const total = order.pricingSummary?.total?.value ?? order.orderTotal;

  const currency =
    order.pricingSummary?.total?.currency ?? order.currency ?? 'USD';

  return (
    <Box>
      <Breadcrumb
        spacing="8px"
        separator={<ChevronRightIcon color="gray.500" />}
        fontSize="sm"
        mb={4}
      >
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/admin/orders">
            Orders
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>{order.marketplaceOrderId}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Box
        bg={bgColor}
        borderRadius="xl"
        p={6}
        border="1px solid"
        borderColor={borderColor}
        mb={6}
      >
        <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
          <HStack spacing={4} align="start">
            <Image
              src={getMarketplaceLogo(order.marketplace)}
              alt={order.marketplace}
              boxSize="48px"
              objectFit="contain"
            />
            <VStack align="start" spacing={1}>
              <HStack spacing={2} flexWrap="wrap">
                <Text fontSize="2xl" fontWeight="bold" color={headingColor} fontFamily="mono">
                  {order.marketplaceOrderId}
                </Text>
                {extUrl && (
                  <Link href={extUrl} isExternal>
                    <ExternalLinkIcon />
                  </Link>
                )}
              </HStack>
              <HStack spacing={2} flexWrap="wrap">
                <Badge colorScheme={mainStatus.colorScheme} variant="solid" px={3} py={0.5}>
                  {mainStatus.label}
                </Badge>
                <Badge colorScheme={fulfillment.colorScheme} variant="subtle" px={3} py={0.5}>
                  {fulfillment.label}
                </Badge>
                <Badge colorScheme={payment.colorScheme} variant="subtle" px={3} py={0.5}>
                  {payment.label}
                </Badge>
                {order.programs?.includes('PRIME') && (
                  <Badge colorScheme="blue">Prime</Badge>
                )}
                {order.isBusinessOrder && (
                  <Badge colorScheme="purple">Business</Badge>
                )}
                {order.fulfillmentChannel && (
                  <Badge variant="outline" textTransform="uppercase">
                    {order.fulfillmentChannel}
                  </Badge>
                )}
              </HStack>
              <Text fontSize="sm" color={subTextColor}>
                {order.storeName}
                {order.salesRecordReference && ` · Sales record ${order.salesRecordReference}`}
              </Text>
            </VStack>
          </HStack>

          <VStack align={{ base: 'start', md: 'end' }} spacing={1}>
            <Text fontSize="sm" color={subTextColor}>
              Order total
            </Text>
            <Text fontSize="3xl" fontWeight="bold" color={headingColor}>
              {formatMoney(total, currency)}
            </Text>
            <Text fontSize="xs" color={subTextColor}>
              Placed {formatOrderDate(order.createdAt)}
            </Text>
          </VStack>
        </Flex>

        <Divider my={4} />

        <HStack spacing={8} flexWrap="wrap" fontSize="sm">
          <Box>
            <Text color={subTextColor} fontSize="xs">
              Last updated
            </Text>
            <Text color={textColor} fontWeight="medium">
              {formatOrderDate(order.lastModifiedDate || order.lastUpdatedAt)}
            </Text>
          </Box>
          {order.linkedSku?.canonicalSku && (
            <Box>
              <Text color={subTextColor} fontSize="xs">
                Linked SKU
              </Text>
              <Button
                as={RouterLink}
                to="/admin/items"
                size="xs"
                variant="link"
                colorScheme="blue"
                fontFamily="mono"
              >
                {order.linkedSku.canonicalSku}
              </Button>
            </Box>
          )}
          {order.salesChannel?.marketplaceName && (
            <Box>
              <Text color={subTextColor} fontSize="xs">
                Sales channel
              </Text>
              <Text color={textColor}>{order.salesChannel.marketplaceName}</Text>
            </Box>
          )}
        </HStack>
      </Box>
    </Box>
  );
};

export default OrderHeader;
